import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useFetchPrograms from "../../hooks/useFetchPrograms"; // Custom hook
import { useTranslation } from "react-i18next";
import "bootstrap/dist/css/bootstrap.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./Programs.css"; // Custom styles
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TABS = ["all", "diplomas", "courses"];

const Programs = () => {
  const {
    diplomas = [],
    courses = [],
    loadingDiplomas,
    loadingCourses,
    errorDiplomas,
    errorCourses,
  } = useFetchPrograms();

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [view, setView] = useState("all");
  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 6;

  const isValid = useCallback((value) => {
    return typeof value === "string" && value.trim() !== "" && value.trim().toLowerCase() !== "n/a";
  }, []);

  const localized = useCallback(
    (field) => field?.[i18n.language] || field?.["en"] || "",
    [i18n.language]
  );

  // Normalise diplomas and courses into one card shape
  const programs = useMemo(() => {
    const fromDiplomas = diplomas
      .filter((d) => isValid(localized(d.name)) && isValid(localized(d.description)) && d.duration)
      .map((d) => ({
        id: d.id,
        kind: "diplomas",
        title: localized(d.name),
        text: localized(d.description),
        duration: d.duration,
        level: d.level,
      }));
    const fromCourses = courses
      .filter((c) => isValid(localized(c.title)) && isValid(localized(c.subtitle)) && c.duration)
      .map((c) => ({
        id: c.id,
        kind: "courses",
        title: localized(c.title),
        text: localized(c.subtitle),
        duration: c.duration,
        level: c.level,
      }));
    return [...fromDiplomas, ...fromCourses];
  }, [diplomas, courses, isValid, localized]);

  const { displayedContent, totalPages, totalResults } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = programs.filter(
      (p) =>
        (view === "all" || p.kind === view) &&
        (!q || p.title.toLowerCase().includes(q) || p.text.toLowerCase().includes(q))
    );
    const startIndex = (currentPage - 1) * itemsPerPage;
    return {
      displayedContent: filtered.slice(startIndex, startIndex + itemsPerPage),
      totalPages: Math.ceil(filtered.length / itemsPerPage),
      totalResults: filtered.length,
    };
  }, [programs, view, query, currentPage]);

  const handleViewToggle = (selectedView) => {
    setView(selectedView);
    setCurrentPage(1);
  };

  const handleQuery = (e) => {
    setQuery(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (i18n.language === "ar") {
      document.body.dir = "rtl";
      document.body.lang = "ar";
    } else {
      document.body.dir = "ltr";
      document.body.lang = "en";
    }

    if (errorDiplomas) {
      toast.error(t("errorFetchingDiplomas"));
    }
    if (errorCourses) {
      toast.error(t("errorFetchingCourses"));
    }
  }, [i18n.language, errorDiplomas, errorCourses, t]);

  const formatDuration = (duration) => {
    if (typeof duration !== "object" || duration === null) return t("notAvailable");
    const { amount, unit } = duration;
    if (amount && unit) {
      const formattedUnit = unit.charAt(0).toUpperCase() + unit.slice(1);
      return `${amount} ${t(formattedUnit.toLowerCase())}`;
    }
    return t("notAvailable");
  };

  const tabLabel = (tab) => (tab === "all" ? t("all", "All") : t(tab));
  const loading = loadingDiplomas || loadingCourses;

  return (
    <div className="programs-page">
      <section className="programs-section container">
        <div className="text-center">
          <span className="ccf-eyebrow">{t("diplomas")} &amp; {t("courses")}</span>
          <h2 className="programs-heading">{t("exploreOurPrograms")}</h2>
          <div className="heading-line"></div>
        </div>

        {/* Search + type tabs */}
        <div className="programs-toolbar">
          <div className="programs-search">
            <i className="fas fa-search" aria-hidden="true"></i>
            <input
              type="search"
              className="form-control"
              placeholder={t("searchPrograms", "Search programs…")}
              aria-label={t("searchPrograms", "Search programs…")}
              value={query}
              onChange={handleQuery}
            />
          </div>

          <div className="programs-tabs" role="tablist">
            {TABS.map((tab) => (
              <button
                key={tab}
                role="tab"
                aria-selected={view === tab}
                className={`programs-tab ${view === tab ? "active" : ""}`}
                onClick={() => handleViewToggle(tab)}
              >
                {tabLabel(tab)}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="text-center my-5">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="mt-2">{t("loading")}</p>
          </div>
        )}

        {!loading && (
          <>
            {displayedContent.length > 0 ? (
              <div className="row g-4">
                {displayedContent.map((item) => (
                  <div className="col-lg-4 col-md-6" key={`${item.kind}-${item.id}`}>
                    <article className="program-card">
                      <span className={`program-badge program-badge--${item.kind}`}>
                        {item.kind === "diplomas" ? t("diploma", "Diploma") : t("course", "Course")}
                      </span>
                      <h3 className="program-title">{item.title}</h3>
                      <p className="program-text">{item.text}</p>
                      <div className="program-meta">
                        <span><i className="far fa-clock"></i> {formatDuration(item.duration)}</span>
                        {item.level && <span className="program-level">{item.level}</span>}
                      </div>
                      <button
                        className="program-link"
                        onClick={() =>
                          navigate(`/${item.kind === "diplomas" ? "diploma" : "course"}-details/${item.id}`)
                        }
                      >
                        {t("viewDetails", "View Details")} <i className="fas fa-arrow-right"></i>
                      </button>
                    </article>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center programs-empty">{t("noItemsAvailable")}</p>
            )}

            {totalPages > 1 && (
              <nav className="programs-pager" aria-label="Programs pagination">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                >
                  {t("previous")}
                </button>
                <span>
                  {t("page")} {currentPage} {t("of")} {totalPages} · {totalResults}
                </span>
                <button
                  className="btn btn-outline-primary"
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  {t("next")}
                </button>
              </nav>
            )}
          </>
        )}
      </section>
      <ToastContainer />
    </div>
  );
};

export default Programs;
